import api from "@/plugins/api";
import { logger } from "./LoggerService";
/**
 * API service implementation for recurring payment rules
 * Communicates with the backend REST API
 */
export class ApiRecurringPaymentRuleService {
    constructor() {
        Object.defineProperty(this, "basePath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "/recurring-payment-rules"
        });
    }
    /**
     * Get a single recurring payment rule by ID
     */
    async getById(id) {
        try {
            logger.debug("Fetching recurring payment rule by ID:", { id });
            const response = await api.get(`${this.basePath}/${id}`);
            logger.debug("Recurring payment rule fetched successfully:", response.data);
            return response.data;
        }
        catch (error) {
            if (error?.response?.status === 404) {
                logger.debug("Recurring payment rule not found:", { id });
                return null;
            }
            logger.debug("Failed to fetch recurring payment rule:", error);
            throw error;
        }
    }
    /**
     * Get a paginated list of recurring payment rules
     */
    async getList(page, pageSize, searchTerm, filters) {
        try {
            logger.debug("Fetching recurring payment rules list:", {
                page,
                pageSize,
                searchTerm,
                filters,
            });
            const params = { page, pageSize };
            if (searchTerm && searchTerm.trim()) {
                params.searchTerm = searchTerm.trim();
            }
            if (filters?.subscriptionId) {
                params.subscriptionId = filters.subscriptionId;
            }
            if (filters?.merchantReference) {
                params.merchantReference = filters.merchantReference;
            }
            if (filters?.statuses?.length) {
                params.statuses = filters.statuses.join(",");
            }
            if (filters?.frequencies?.length) {
                params.frequencies = filters.frequencies.join(",");
            }
            if (filters?.amountMin !== undefined) {
                params.amountMin = filters.amountMin;
            }
            if (filters?.amountMax !== undefined) {
                params.amountMax = filters.amountMax;
            }
            if (filters?.firstAmountMin !== undefined) {
                params.firstAmountMin = filters.firstAmountMin;
            }
            if (filters?.firstAmountMax !== undefined) {
                params.firstAmountMax = filters.firstAmountMax;
            }
            if (filters?.transactionCountMin !== undefined) {
                params.transactionCountMin = filters.transactionCountMin;
            }
            if (filters?.transactionCountMax !== undefined) {
                params.transactionCountMax = filters.transactionCountMax;
            }
            if (filters?.dateFrom) {
                params.dateFrom = filters.dateFrom;
            }
            if (filters?.dateTo) {
                params.dateTo = filters.dateTo;
            }
            if (filters?.sortBy) {
                params.sortBy = filters.sortBy;
            }
            if (filters?.sortOrder) {
                params.sortOrder = filters.sortOrder;
            }
            const response = await api.get(`${this.basePath}/list`, { params });
            logger.debug("Recurring payment rules list fetched successfully:", response.data);
            // Map backend DTO to frontend interface
            const backendData = response.data;
            return {
                items: backendData.data || [],
                totalCount: backendData.totalCount || 0,
                page: backendData.currentPage || page,
                pageSize: backendData.pageSize || pageSize,
                hasNextPage: backendData.hasNextPage || false,
                hasPreviousPage: backendData.hasPreviousPage || false,
            };
        }
        catch (error) {
            logger.debug("Failed to fetch recurring payment rules list:", error);
            throw error;
        }
    }
    /**
     * Get faceted filter counts for recurring payment rules
     */
    async getFilterCounts(filters) {
        try {
            logger.debug("Fetching recurring payment rule filter counts:", { filters });
            const params = {};
            if (filters?.subscriptionId) {
                params.subscriptionId = filters.subscriptionId;
            }
            if (filters?.merchantReference) {
                params.merchantReference = filters.merchantReference;
            }
            if (filters?.statuses?.length) {
                params.statuses = filters.statuses.join(",");
            }
            if (filters?.frequencies?.length) {
                params.frequencies = filters.frequencies.join(",");
            }
            if (filters?.amountMin !== undefined) {
                params.amountMin = filters.amountMin;
            }
            if (filters?.amountMax !== undefined) {
                params.amountMax = filters.amountMax;
            }
            if (filters?.firstAmountMin !== undefined) {
                params.firstAmountMin = filters.firstAmountMin;
            }
            if (filters?.firstAmountMax !== undefined) {
                params.firstAmountMax = filters.firstAmountMax;
            }
            if (filters?.transactionCountMin !== undefined) {
                params.transactionCountMin = filters.transactionCountMin;
            }
            if (filters?.transactionCountMax !== undefined) {
                params.transactionCountMax = filters.transactionCountMax;
            }
            if (filters?.dateFrom) {
                params.dateFrom = filters.dateFrom;
            }
            if (filters?.dateTo) {
                params.dateTo = filters.dateTo;
            }
            const response = await api.get(`${this.basePath}/filter-counts`, { params });
            logger.debug("Filter counts fetched successfully:", response.data);
            return response.data;
        }
        catch (error) {
            logger.debug("Failed to fetch filter counts:", error);
            throw error;
        }
    }
    /**
     * Cancel a recurring payment rule (subscription)
     */
    async cancel(id, cancellationReason) {
        try {
            logger.debug("Cancelling recurring payment rule:", { id, cancellationReason });
            const response = await api.post(`${this.basePath}/${id}/cancel`, {
                cancellationReason,
            });
            logger.debug("Recurring payment rule cancelled successfully:", response.data);
            return response.data;
        }
        catch (error) {
            logger.debug("Failed to cancel recurring payment rule:", error);
            throw error;
        }
    }
    /**
     * Pause an active recurring payment rule (temporarily stops future transactions)
     */
    async pause(id) {
        try {
            logger.debug("Pausing recurring payment rule:", { id });
            await api.post(`${this.basePath}/${id}/pause`);
            logger.debug("Recurring payment rule paused successfully");
        }
        catch (error) {
            logger.debug("Failed to pause recurring payment rule:", error);
            throw error;
        }
    }
    /**
     * Resume a paused recurring payment rule (restarts future transactions)
     */
    async resume(id) {
        try {
            logger.debug("Resuming recurring payment rule:", { id });
            await api.post(`${this.basePath}/${id}/resume`);
            logger.debug("Recurring payment rule resumed successfully");
        }
        catch (error) {
            logger.debug("Failed to resume recurring payment rule:", error);
            throw error;
        }
    }
    /**
     * Cancel only the next scheduled payment
     */
    async cancelNext(id) {
        try {
            logger.debug("Cancelling next payment for recurring payment rule:", { id });
            const response = await api.post(`${this.basePath}/${id}/cancel-next`);
            logger.debug("Next payment cancelled successfully:", response.data);
            return response.data;
        }
        catch (error) {
            logger.debug("Failed to cancel next payment:", error);
            throw error;
        }
    }
    /**
     * Update a recurring payment rule configuration
     */
    async update(request) {
        try {
            logger.debug("Updating recurring payment rule:", request);
            const { id, ...updateData } = request;
            const response = await api.put(`${this.basePath}/${id}`, updateData);
            logger.debug("Recurring payment rule updated successfully:", response.data);
            return response.data;
        }
        catch (error) {
            logger.debug("Failed to update recurring payment rule:", error);
            throw error;
        }
    }
}
// Export singleton instance
export const recurringPaymentRuleService = new ApiRecurringPaymentRuleService();
